import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Edit, 
  Plus, 
  Trash2, 
  Search, 
  Image, 
  Settings, 
  Eye, 
  Menu,
  X,
  Users,
  UserCheck,
  Shield,
  MapPin,
  Briefcase,
  Calendar,
  Phone,
  CreditCard,
  Star,
  Bell,
  BarChart3,
  LogOut,
  Command,
  Layout,
  BookOpen,
  Upload,
  Link,
  Clock,
  User,
  RefreshCw,
  File
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ContentEditor from './ContentEditor';
import AdminLayout from '../common/AdminLayout';
import Swal from 'sweetalert2';

interface CMSContent {
  id: string;
  title: string;
  type: 'page' | 'post' | 'media' | 'template';
  status: 'published' | 'draft' | 'archived';
  author: string;
  lastModified: string;
  content?: string;
  excerpt?: string;
  url?: string;
  tags?: string[];
  mediaType?: string;
  size?: string;
}

interface CommandItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  action: () => void;
  shortcut?: string;
  category: string;
}

const CMS: React.FC = () => {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('pages');
  const [searchTerm, setSearchTerm] = useState('');
  const [commandSearch, setCommandSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [cmsContent, setCmsContent] = useState<CMSContent[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<CMSContent | null>(null);
  const [isNewContent, setIsNewContent] = useState(false);
  
  const { admin } = useAuth();

  // Fetch CMS content from API
  const fetchContent = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (activeTab !== 'all' && activeTab !== 'pages' && activeTab !== 'posts' && activeTab !== 'media' && activeTab !== 'templates') {
        // Don't add type filter for tab-specific views
      } else if (activeTab === 'pages') {
        params.append('type', 'page');
      } else if (activeTab === 'posts') {
        params.append('type', 'post');
      } else if (activeTab === 'media') {
        params.append('type', 'media');
      } else if (activeTab === 'templates') {
        params.append('type', 'template');
      }
      
      if (searchTerm) params.append('search', searchTerm);

      const token = localStorage.getItem('skillseek_admin_token') || localStorage.getItem('token');
      console.log('Fetching CMS content with token:', token ? 'Token present' : 'No token found');

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/cms?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('CMS fetch response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('CMS content fetched:', data);
        setCmsContent(data.content || []);
      } else {
        const errorData = await response.json().catch(() => null);
        console.error('Failed to fetch content:', response.status, errorData);
        
        if (response.status === 401) {
          console.error('Authentication failed - user needs to log in');
        }
        // Keep existing content if API fails
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      // Keep existing content if API fails
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, [activeTab, searchTerm]);

  // Command palette items
  const commandItems: CommandItem[] = [
    { id: 'new-page', label: 'New Page', icon: Plus, action: () => handleNewContent('page'), shortcut: 'Ctrl+N', category: 'Create' },
    { id: 'new-post', label: 'New Post', icon: Edit, action: () => handleNewContent('post'), shortcut: 'Ctrl+Shift+N', category: 'Create' },
    { id: 'upload-media', label: 'Upload Media', icon: Upload, action: () => handleUploadMedia(), shortcut: 'Ctrl+U', category: 'Create' },
    { id: 'new-template', label: 'New Template', icon: Layout, action: () => handleNewContent('template'), category: 'Create' },
    { id: 'go-pages', label: 'Go to Pages', icon: BookOpen, action: () => setActiveTab('pages'), shortcut: '1', category: 'Navigate' },
    { id: 'go-posts', label: 'Go to Posts', icon: FileText, action: () => setActiveTab('posts'), shortcut: '2', category: 'Navigate' },
    { id: 'go-media', label: 'Go to Media', icon: Image, action: () => setActiveTab('media'), shortcut: '3', category: 'Navigate' },
    { id: 'go-templates', label: 'Go to Templates', icon: Layout, action: () => setActiveTab('templates'), shortcut: '4', category: 'Navigate' },
    { id: 'search', label: 'Search Content', icon: Search, action: () => document.getElementById('search-input')?.focus(), shortcut: 'Ctrl+K', category: 'Actions' },
    { id: 'refresh', label: 'Refresh Content', icon: RefreshCw, action: () => fetchContent(), shortcut: 'F5', category: 'Actions' },
    { id: 'settings', label: 'CMS Settings', icon: Settings, action: () => {}, category: 'Actions' },
  ];

  const filteredCommands = commandItems.filter(item =>
    item.label.toLowerCase().includes(commandSearch.toLowerCase()) ||
    item.category.toLowerCase().includes(commandSearch.toLowerCase())
  );

  const handleNewContent = (type: CMSContent['type']) => {
    setEditingContent({
      id: Date.now().toString(),
      title: '',
      type,
      status: 'draft',
      author: admin?.email || 'Admin',
      lastModified: new Date().toISOString(),
      content: '',
      url: ''
    });
    setIsNewContent(true);
    setEditorOpen(true);
    setCommandPaletteOpen(false);
  };

  const handleEditContent = (content: CMSContent) => {
    setEditingContent(content);
    setIsNewContent(false);
    setEditorOpen(true);
  };

  const handleViewContent = (content: CMSContent) => {
    if (content.type === 'page') {
      const slug = content.url?.replace('/', '') || content.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      window.open(`/page/${slug}`, '_blank');
    } else if (content.type === 'post') {
      const slug = content.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      window.open(`/posts/${slug}`, '_blank');
    }
  };

  const handleSaveContent = async (savedContent: CMSContent) => {
    console.log('CMS handleSaveContent called with:', savedContent);
    
    // Update local state immediately for better UX
    if (isNewContent) {
      setCmsContent(prev => [...prev, savedContent]);
    } else {
      setCmsContent(prev => prev.map(item => 
        item.id === savedContent.id ? savedContent : item
      ));
    }
    
    // Refresh content to get latest data from server
    setTimeout(() => {
      fetchContent();
    }, 500);
  };

  const handleDeleteContent = async (contentId: string) => {
    const result = await Swal.fire({
      title: 'Confirm Delete',
      text: 'Are you sure you want to delete this content?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/cms/${contentId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (response.ok) {
          setCmsContent(prev => prev.filter(item => item.id !== contentId));
          // Refresh content to get latest data
          fetchContent();
          Swal.fire({
            title: 'Success!',
            text: 'Content deleted successfully',
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#28a745'
          });
        } else {
          console.error('Failed to delete content:', response.statusText);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to delete content. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#dc3545'
          });
        }
      } catch (error) {
        console.error('Error deleting content:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Error deleting content. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      }
    }
  };

  const handleUploadMedia = () => {
    console.log('Opening media upload');
    setCommandPaletteOpen(false);
  };

  const handleLogout = () => {
    // Logout functionality handled by AdminLayout
  };

  const sidebarItems = [
    { icon: BarChart3, label: 'Dashboard', href: '/dashboard', active: true },
    { icon: MapPin, label: 'Heat Map View', href: '/heat-map' },
    { icon: Users, label: 'Manage Clients', href: '/clients' },
    { icon: UserCheck, label: 'Manage Talent', href: '/talent' },
    { icon: Shield, label: 'Sub-Admins', href: '/sub-admins' },
    { icon: MapPin, label: 'Service Areas', href: '/service-areas' },
    { icon: Briefcase, label: 'Categories & Skills', href: '/categories' },
    // { icon: Calendar, label: 'Bookings', href: '/bookings' },
    // { icon: Phone, label: 'Emergency Contacts', href: '/emergency-contacts' },
    // { icon: CreditCard, label: 'Payments', href: '/payments' },
    // { icon: Star, label: 'Subscriptions', href: '/subscriptions' },
    { icon: FileText, label: 'CMS', href: '/cms' },
    { icon: Bell, label: 'Notifications', href: '/notifications' },
    { icon: Settings, label: 'Settings', href: '/settings' },
  ];

  const getFilteredContent = () => {
    let filtered = cmsContent;
    
    // Filter by tab
    switch (activeTab) {
      case 'pages':
        filtered = filtered.filter(item => item.type === 'page');
        break;
      case 'posts':
        filtered = filtered.filter(item => item.type === 'post');
        break;
      case 'media':
        filtered = filtered.filter(item => item.type === 'media');
        break;
      case 'templates':
        filtered = filtered.filter(item => item.type === 'template');
        break;
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.content && item.content.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    return filtered;
  };

  const getStatusColor = (status: CMSContent['status']) => {
    switch (status) {
      case 'published':
        return 'bg-success text-white';
      case 'draft':
        return 'bg-warning text-dark';
      case 'archived':
        return 'bg-secondary text-white';
      default:
        return 'bg-secondary text-white';
    }
  };

  const getTypeIcon = (type: CMSContent['type']) => {
    switch (type) {
      case 'page':
        return BookOpen;
      case 'post':
        return FileText;
      case 'media':
        return Image;
      case 'template':
        return Layout;
      default:
        return File;
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
      
      if (e.key === 'Escape') {
        setCommandPaletteOpen(false);
      }
      
      if (e.key >= '1' && e.key <= '4' && !e.ctrlKey && !e.metaKey) {
        const tabs = ['pages', 'posts', 'media', 'templates'];
        const tabIndex = parseInt(e.key) - 1;
        if (tabs[tabIndex]) {
          setActiveTab(tabs[tabIndex]);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (loading) {
    return (
      <AdminLayout title="Content Management System" subtitle="Manage your website content and media">
        <div className="loading-spinner" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Content Management System" subtitle="Manage your website content and media">
      <div className="fade-in">
        {/* Debug Info */}
   
        {/* Header Actions */}
        <div className="admin-card" style={{ marginBottom: '25px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
              {/* Tabs */}
              <div style={{ display: 'flex', gap: '10px' }}>
                {[
                  { id: 'pages', label: 'Pages', icon: BookOpen },
                  { id: 'posts', label: 'Posts', icon: FileText },
                  { id: 'media', label: 'Media', icon: Image },
                  { id: 'templates', label: 'Templates', icon: Layout },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={activeTab === tab.id ? 'btn-primary' : 'btn-secondary'}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <tab.icon size={16} />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div style={{ position: 'relative', minWidth: '300px' }}>
                <input
                  id="search-input"
                  type="text"
                  placeholder="Search content..."
                  className="form-control"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: '40px' }}
                />
                <Search size={18} style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: 'var(--text-dark-gray)'
                }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setCommandPaletteOpen(true)}
                className="btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Command size={16} />
                Commands
              </button>
              <button
                onClick={() => handleNewContent(activeTab as CMSContent['type'])}
                className="btn-primary"
              >
                <Plus size={18} />
                New {activeTab.charAt(0).toUpperCase() + activeTab.slice(1, -1)}
              </button>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="admin-card">
          {getFilteredContent().length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <File size={48} style={{ color: 'var(--text-light-gray)', marginBottom: '20px' }} />
              <h4 style={{ color: 'var(--text-color)', marginBottom: '10px' }}>No {activeTab} found</h4>
              <p style={{ color: 'var(--text-dark-gray)', marginBottom: '30px' }}>
                {searchTerm 
                  ? 'Try adjusting your search terms.' 
                  : `Create your first ${activeTab.slice(0, -1)} to get started.`
                }
              </p>
              {!searchTerm && (
                <button
                  onClick={() => handleNewContent(activeTab as CMSContent['type'])}
                  className="btn-primary"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 auto' }}
                >
                  <Plus size={16} />
                  Create {activeTab.charAt(0).toUpperCase() + activeTab.slice(1, -1)}
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '20px' }}>
              {getFilteredContent().map((item) => {
                const IconComponent = getTypeIcon(item.type);
                return (
                  <div key={item.id} className="content-item-card">
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
                        <div style={{ 
                          width: '50px', 
                          height: '50px', 
                          backgroundColor: 'var(--bg-light)', 
                          borderRadius: '8px',
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <IconComponent size={24} style={{ color: 'var(--text-dark-gray)' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h5 style={{ color: 'var(--text-color)', marginBottom: '8px', fontSize: '1.1rem' }}>
                            {item.title}
                          </h5>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--text-dark-gray)', fontSize: '0.9rem' }}>
                              <User size={14} />
                              {item.author}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--text-dark-gray)', fontSize: '0.9rem' }}>
                              <Clock size={14} />
                              {new Date(item.lastModified).toLocaleDateString()}
                            </span>
                            {item.url && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--text-dark-gray)', fontSize: '0.9rem' }}>
                                <Link size={14} />
                                {item.url}
                              </span>
                            )}
                            {item.size && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--text-dark-gray)', fontSize: '0.9rem' }}>
                                <File size={14} />
                                {item.size}
                              </span>
                            )}
                          </div>
                          {item.content && (
                            <p style={{ 
                              color: 'var(--text-dark-gray)', 
                              fontSize: '0.9rem', 
                              marginTop: '10px',
                              marginBottom: '0',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>
                              {item.content}
                            </p>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, marginLeft: '15px' }}>
                        <span className={`status-badge status-${item.status}`}>
                          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </span>
                        
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button 
                            className="btn-icon"
                            onClick={() => handleViewContent(item)}
                            title="View"
                          >
                            <Eye size={16} />
                          </button>
                          <button 
                            className="btn-icon"
                            onClick={() => handleEditContent(item)}
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button 
                            className="btn-icon btn-icon-danger"
                            onClick={() => handleDeleteContent(item.id)}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Command Palette Modal */}
        {commandPaletteOpen && (
          <div className="modal-overlay" onClick={() => setCommandPaletteOpen(false)}>
            <div className="modal-content command-palette" onClick={(e) => e.stopPropagation()}>
              <div className="command-palette-header">
                <Command size={20} style={{ color: 'var(--text-dark-gray)' }} />
                <input
                  type="text"
                  placeholder="Type a command or search..."
                  value={commandSearch}
                  onChange={(e) => setCommandSearch(e.target.value)}
                  className="command-search-input"
                  autoFocus
                />
                <button
                  onClick={() => setCommandPaletteOpen(false)}
                  className="btn-icon"
                  style={{ marginLeft: 'auto' }}
                >
                  <X size={16} />
                </button>
              </div>

              <div className="command-palette-body">
                {Object.entries(
                  filteredCommands.reduce((acc, item) => {
                    if (!acc[item.category]) acc[item.category] = [];
                    acc[item.category].push(item);
                    return acc;
                  }, {} as Record<string, CommandItem[]>)
                ).map(([category, items]) => (
                  <div key={category} className="command-category">
                    <div className="command-category-header">
                      {category}
                    </div>
                    {items.map((item) => (
                      <button
                        key={item.id}
                        onClick={item.action}
                        className="command-item"
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <item.icon size={16} style={{ color: 'var(--text-dark-gray)' }} />
                          <span>{item.label}</span>
                        </div>
                        {item.shortcut && (
                          <kbd className="command-shortcut">
                            {item.shortcut}
                          </kbd>
                        )}
                      </button>
                    ))}
                  </div>
                ))}

                {filteredCommands.length === 0 && (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-dark-gray)' }}>
                    No commands found matching "{commandSearch}"
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Content Editor Modal */}
        <ContentEditor
          isOpen={editorOpen}
          onClose={() => {
            setEditorOpen(false);
            setEditingContent(null);
            setIsNewContent(false);
          }}
          content={editingContent}
          onSave={handleSaveContent}
          isNew={isNewContent}
        />
      </div>
    </AdminLayout>
  );
};

export default CMS;